import type { DraftLineItem, Order, Shop } from "../types";

type ShippingRule = "never" | "firstLineItem" | "lastLineItem";

/** Only a refund or a store credit gives the initial shipping back. */
const REFUNDABLE_RESOLUTIONS = new Set(["refund", "discount"]);

/**
 * Initial shipping amount given back to the customer, in cents.
 *
 * Transposition of `apps/return-widget/src/utils/get-initial-shipping-refund-amount.ts`.
 * `lastLineItem` only pays out when the whole order is being returned; `fixedAmount`
 * caps the payout and a negative value means "no cap".
 */
export function getInitialShippingRefundInCents(
  draftLineItems: DraftLineItem[],
  order: Order,
  shop: Shop,
): number {
  const shipping = order.financialInformations.initial_shipping_refunded;
  const rule = shop.preferences.refundInitialShippingRule as ShippingRule;
  if (rule === "never") return 0;
  if (shipping.done) return Math.round(shipping.amount * 100);

  const refundableItems = draftLineItems.filter(
    (li) =>
      li.resolution &&
      REFUNDABLE_RESOLUTIONS.has(li.resolution) &&
      li.quantity > 0,
  );
  if (refundableItems.length === 0) return 0;

  if (rule === "lastLineItem" && !isCompleteReturn(draftLineItems, order)) {
    return 0;
  }

  const cap =
    shipping.fixedAmount < 0 ? Number.POSITIVE_INFINITY : shipping.fixedAmount;
  return Math.round(Math.min(cap, shipping.presentment_amount) * 100);
}

/** Every returnable line of the order is in the basket, in full. */
function isCompleteReturn(
  draftLineItems: DraftLineItem[],
  order: Order,
): boolean {
  return (order.lineItems ?? []).every((orderLineItem) => {
    const drafted = draftLineItems.find(
      (li) => li.lineItemId === orderLineItem.line_item_id,
    );
    return (drafted?.quantity ?? 0) === orderLineItem.quantity_bought;
  });
}
